// ═══════════════════════════════════════════════════════════════
// use-error-reports — crashes, grouped into bugs.
//
// A raw list of crash reports is close to unreadable: one bad render
// loop writes twenty rows that are all the same thing. What you want
// to know is "how many DIFFERENT things are broken, how often, for
// how many people, on which build" — so the list is grouped by
// fingerprint and sorted by how recently each bug last happened.
// ═══════════════════════════════════════════════════════════════
import { useEffect, useState } from 'react';
import {
  collection, doc, limit, onSnapshot, orderBy, query,
  serverTimestamp, writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase.js';
import { isHandled } from './error-grouping.js';

export { groupReports, reportKey, isHandled } from './error-grouping.js';

// A busy month of crashes still fits well under this, and it caps
// what one bad release can cost to load.
export const REPORT_LIMIT = 500;

export function errorReportsCol() {
  return collection(db, 'errorReports');
}

// Admin-only by the rules — a non-admin subscription errors and
// returns empty rather than throwing.
export function useErrorReports() {
  const [reports, setReports] = useState([]);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(
      query(errorReportsCol(), orderBy('createdAt', 'desc'), limit(REPORT_LIMIT)),
      (snap) => {
        setReports(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setReady(true);
      },
      (err) => {
        console.error('Error reports load failed:', err);
        setError(err);
        setReady(true);
      }
    );
    return unsub;
  }, []);

  return { reports, ready, error };
}

// Mark every occurrence of one bug handled, stamped with the build
// you believe fixes it — so when it comes back you can see whether it
// came back on the fixed build or an old one still in someone's tab.
export async function markGroupHandled(group, appVersion = '', handled = true) {
  const targets = group.reports.filter((r) => isHandled(r) !== handled);
  for (let i = 0; i < targets.length; i += 400) {
    const batch = writeBatch(db);
    targets.slice(i, i + 400).forEach((r) => {
      batch.update(doc(errorReportsCol(), r.id), {
        handledAt: handled ? serverTimestamp() : null,
        handledVersion: handled ? appVersion : '',
      });
    });
    await batch.commit();
  }
  return targets.length;
}
